import React, { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, onSnapshot, query, where, QueryConstraint } from "firebase/firestore";
import { startOfDay, endOfDay } from "date-fns";
import { db } from "@/firebase";
import logo from "@/assets/logo.png";
import IncomingOrderDialog from "./components/IncomingOrderDialog";
import {
  useUpdateOrderStatusMutation,
  useRejectOrderMutation,
} from "@/features/organizations/api/ordersApi";
import { useAppSelector } from "@/hooks";
import useOrganizations from "@/features/organizations/useOrganizations";
import useSnackbar from "@/hooks/useSnackbar";
import { Order, OrderStatus, PaymentMethodsType } from "@/types";
import ChromeFreezeDialog from "./components/ChromeFreezeDialog";
import incomingOrderSound from "@/assets/alerts/IncomingOrder.mp3"
import { LocalStorage } from "@/constants/localStorage";


interface IncomingOrdersContextType {
  orders: Order[];
  openChromeFreezeDialog: () => void;
}

const IncomingOrdersContext = createContext<IncomingOrdersContextType>({
  orders: [],
  openChromeFreezeDialog: () => { },
});

export const useIncomingOrders = () => useContext(IncomingOrdersContext);

export const IncomingOrdersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const { organizationId } = useParams<{ organizationId: string }>();
  const { currentOrganization } = useOrganizations();
  const organizationFromStore = useAppSelector((state) => (organizationId ? state.organizations[organizationId] : null));
  const organization = currentOrganization || organizationFromStore;

  const [orders, setOrders] = useState<Order[]>([]);
  const [orderAlert, setOrderAlert] = useState<Order | null>(null);
  const [alertQueue, setAlertQueue] = useState<Order[]>([]);

  // Estado dinámico para la fecha, para evitar el bug si dejan la pestaña abierta
  const [dayBounds, setDayBounds] = useState(() => ({
    start: startOfDay(new Date()).getTime(),
    end: endOfDay(new Date()).getTime(),
  }));

  const notifiedOrdersRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const [updateOrderStatus] = useUpdateOrderStatusMutation();
  const [rejectOrder] = useRejectOrderMutation();



  const [chromeFreezeDialogOpen, setChromeFreezeDialogOpen] = useState(false);

  const antiFreezeAudioRef = useRef<AudioBuffer | null>(null);
  const incomingOrderAudioRef = useRef<AudioBuffer | null>(null);

  const initializeIncomingOrderAudio = useCallback(async () => {
    try {
      if (!incomingOrderAudioRef.current) {
        const audioContext = new AudioContext();
        const audioFile = await fetch(incomingOrderSound);
        const arrayBuffer = await audioFile.arrayBuffer();

        incomingOrderAudioRef.current =
          await audioContext.decodeAudioData(arrayBuffer);
      }

      console.log("Audio de pedidos preparado");
    } catch (error) {
      console.error("No se pudo inicializar el audio", error);
    }
  }, []);


  const initializeAntiFreezeAudio = useCallback(async () => {
    try {

      if (!antiFreezeAudioRef.current) {
        const audioContext = new AudioContext();
        const audioFile = await fetch(incomingOrderSound);
        const arrayBuffer = await audioFile.arrayBuffer();

        antiFreezeAudioRef.current =
          await audioContext.decodeAudioData(arrayBuffer);
      }

      console.log("Audio de pedidos preparado");
    } catch (error) {
      console.error("No se pudo inicializar el audio", error);
    }
  }, []);

  useEffect(() => {
    initializeIncomingOrderAudio();
    initializeAntiFreezeAudio();
  }, []);

  const playIncomingOrderSound = useCallback(() => {
    try {
      const buffer = incomingOrderAudioRef.current;

      if (!buffer) {
        console.error("Audio no está cargado");
        return;
      }

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;

      const audioContext = new AudioContextClass();

      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => { });
      }

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start(0);

      source.onended = () => {
        audioContext.close().catch(() => { });
      };
    } catch (error) {
      console.error("Error al reproducir sonido de pedido entrante:", error);
    }
  }, []);

  const wasFrozenRef = useRef(false);

  const openChromeFreezeDialog = useCallback(async () => {
    setChromeFreezeDialogOpen(true);
  }, []);



  const closeChromeFreezeDialog = useCallback(() => {
    setChromeFreezeDialogOpen(false);
  }, []);


  const handleChromeFreezeSetupComplete = useCallback(async () => {
    await initializeAntiFreezeAudio();

    localStorage.setItem(
      LocalStorage.CHROME_FREEZE_CONFIGURED,
      "true"
    );

    setChromeFreezeDialogOpen(false);
  }, [initializeAntiFreezeAudio]);

  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) return;

    const handleFreeze = () => {
      wasFrozenRef.current = true;
    };

    const handleResume = () => {
      if (!wasFrozenRef.current) return;

      wasFrozenRef.current = false;
      if (!(localStorage.getItem(LocalStorage.CHROME_FREEZE_CONFIGURED) === "true")) {
        setChromeFreezeDialogOpen(true);
      }
    };

    document.addEventListener("freeze", handleFreeze);
    document.addEventListener("resume", handleResume);

    return () => {
      document.removeEventListener("freeze", handleFreeze);
      document.removeEventListener("resume", handleResume);
    };
  }, []);

  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) return;

    const configured =
      localStorage.getItem(LocalStorage.CHROME_FREEZE_CONFIGURED) === "true";

    if (!configured) {
      setChromeFreezeDialogOpen(true);
    }
  }, []);


  useEffect(() => {
    if (!("Notification" in window)) return;

    try {
      if (Notification.permission === "default") {
        Notification.requestPermission().catch(() => { });
      }
    } catch (error) {
      console.warn("Notification.requestPermission no soportado:", error);
    }
  }, []);

  // Intervalo para verificar si cambió el día (cada minuto)
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date();
      const currentStart = startOfDay(now).getTime();

      setDayBounds((prev) => {
        // Si el día cambió, actualizamos los rangos para forzar un re-fetch a Firestore
        if (prev.start !== currentStart) {
          return {
            start: currentStart,
            end: endOfDay(now).getTime(),
          };
        }
        return prev;
      });
    }, 60000); // 1 minuto

    return () => clearInterval(intervalId);
  }, []);

  const showIncomingOrderNotification = useCallback((order: Order) => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    try {
      const notification = new Notification("Pedido Entrante", {
        body: `Pedido #${order.number || order.id}`,
        icon: logo,
        tag: order.id,
        requireInteraction: true,
      });

      notification.onclick = () => {
        navigate(`/comercios/${organizationId}/pedidos/${order.id}`);
        window.focus();
      };
    } catch (error) {
      console.warn("Notification constructor falló (común en Android Chrome):", error);
      if ("serviceWorker" in navigator && navigator.serviceWorker.ready) {
        navigator.serviceWorker.ready
          .then((registration) => {
            registration.showNotification("Pedido Entrante", {
              body: `Pedido #${order.number || order.id}`,
              icon: logo,
              tag: order.id,
              data: { url: `/comercios/${organizationId}/pedidos/${order.id}` },
            }).catch(() => { });
          })
          .catch(() => { });
      }
    }
  }, [navigate, organizationId]);

  const handleUpdateorderStatus = async (status: OrderStatus) => {
    const currentOrderId = orderAlert?.id;

    if (!organizationId || !currentOrderId) return;

    try {
      await updateOrderStatus({
        organizationId,
        orderId: currentOrderId,
        status,
      });

      setOrderAlert(null);
      openSnackbar("Orden actualizada", "success");
    } catch (error) {
      console.error(error);
      openSnackbar("Error al actualizar la orden", "error");
      throw error;
    }
  };

  const handleAcceptOrder = async () => {
    const currentOrderId = orderAlert?.id;
    try {
      const stripeEnabled = Boolean(organization?.settings?.paymentMethods?.stripeEnabled);
      const isCardPayment = orderAlert?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD;
      const isAlreadyPaid = orderAlert?.paymentDetails?.status === "paid" || Boolean(orderAlert?.paidAt);

      const nextStatus =
        stripeEnabled && isCardPayment && !isAlreadyPaid
          ? OrderStatus.PENDING_PAYMENT
          : OrderStatus.PREPARING;

      await handleUpdateorderStatus(nextStatus);

      const hasMorePending = alertQueue.some(qOrder =>
        orders.some(o => o.id === qOrder.id && o.id !== currentOrderId)
      );

      if (!hasMorePending) {
        const shouldNavigate = window.confirm("¿Desea navegar a la página de pedidos entrantes?");
        if (shouldNavigate) {
          navigate(`/comercios/${organizationId}/pedidos`);
        }
      }
    } catch (e) {
      // Error is handled in handleUpdateorderStatus
    }
  };

  const handleRejectOrder = async (rejectedReason: string) => {
    const orderToReject = orderAlert;

    if (!organizationId || !orderToReject?.id) return;

    try {
      await rejectOrder({
        organizationId,
        orderId: orderToReject.id,
        order: orderToReject,
        rejectedReason,
      });

      setOrderAlert(null);
      openSnackbar("Orden rechazada", "success");
    } catch (error) {
      console.error(error);
      openSnackbar("Error al rechazar la orden", "error");
    }
  };

  const handleOnClose = (_: any, reason: "backdropClick") => {
    if (reason !== "backdropClick") {
      setOrderAlert(null);
    }
  };


  useEffect(() => {
    if (!initializedRef.current) {
      orders.forEach((order) => {
        if (order.id) notifiedOrdersRef.current.add(order.id);
      });

      initializedRef.current = true;
      return;
    }

    const newOrders = orders.filter(
      (o) => o.id && !notifiedOrdersRef.current.has(o.id)
    );

    if (!newOrders.length) return;

    const newestOrder = newOrders.reduce((prev, curr) =>
      prev.lastUpdatedAt > curr.lastUpdatedAt ? prev : curr
    );

    newOrders.forEach((order) => {
      if (order.id) {
        notifiedOrdersRef.current.add(order.id)
      };
    });

    showIncomingOrderNotification(newestOrder);
    playIncomingOrderSound();

    setAlertQueue((prev) => [...prev, ...newOrders]);
  }, [orders, showIncomingOrderNotification]);

  useEffect(() => {
    if (orderAlert) return;

    if (alertQueue.length > 0) {
      const nextValidIndex = alertQueue.findIndex(qOrder =>
        orders.some(o => o.id === qOrder.id)
      );

      if (nextValidIndex !== -1) {
        const nextOrder = alertQueue[nextValidIndex];
        setAlertQueue(prev => prev.slice(nextValidIndex + 1));

        setTimeout(() => {
          setOrderAlert(nextOrder);
        }, 100);
      } else {
        setAlertQueue([]);
      }
    }
  }, [orderAlert, alertQueue, orders]);

  useEffect(() => {
    const reviewingIds = new Set(orders.map((o) => o.id));

    notifiedOrdersRef.current.forEach((id) => {
      if (!reviewingIds.has(id)) {
        notifiedOrdersRef.current.delete(id);
      }
    });
  }, [orders]);

  useEffect(() => {
    if (!orderAlert) return;

    const currentOrder = orders.find(
      (o) => o.id === orderAlert.id
    );

    if (!currentOrder) {
      setOrderAlert(null);
    }
  }, [orders, orderAlert]);

  useEffect(() => {
    if (!organizationId) return;

    const ordersRef = collection(db, `organizations/${organizationId}/orders`);

    const conditions: QueryConstraint[] = [
      where("orderedAt", ">=", dayBounds.start),
      where("orderedAt", "<=", dayBounds.end),
      where("status", "==", OrderStatus.REVIEWING),
    ];

    const ordersQuery = query(ordersRef, ...conditions);

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const data: Order[] = [];

        snapshot.forEach((doc) => {
          const order = { ...doc.data(), id: doc.id } as Order;
          const branchMatches = !activeBranch || order.branch?.id === activeBranch || (order as any).branchId === activeBranch;
          if (branchMatches) {
            data.push(order);
          }
        });

        setOrders(data);
      },
      (error) => {
        console.warn("IncomingOrders listener error:", error);
      }
    );

    return () => unsubscribe();
  }, [organizationId, activeBranch, dayBounds.start, dayBounds.end]);


  const contextValue = useMemo(
    () => ({
      orders,
      openChromeFreezeDialog,
    }),
    [orders, openChromeFreezeDialog]
  );

  return (
    <IncomingOrdersContext.Provider value={contextValue}>
      {children}
      <IncomingOrderDialog
        open={orderAlert !== null}
        order={orderAlert}
        onAcceptOrder={handleAcceptOrder}
        onRejectOrder={handleRejectOrder}
        onClose={handleOnClose}
      />

      <ChromeFreezeDialog
        open={chromeFreezeDialogOpen}
        onClose={closeChromeFreezeDialog}
        onConfirm={handleChromeFreezeSetupComplete}
      />
    </IncomingOrdersContext.Provider>
  );
};
